import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldCheck } from '@fortawesome/pro-regular-svg-icons';

import { applyTermPercentage, SaleProduct } from '@v2/shared/lib/megaSale';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';

import useProductTile from './useProductTile';

type Props = {
  product: SaleProduct;
  onAdd: (
    product: SaleProduct,
    skuCode: string,
    giftSkuCode: string | null,
    offerCode: string | null
  ) => Promise<void>;
};

/**
 * Magzter Gold: unlimited digital access, sold as one card rather than as a tile.
 *
 * It arrives in the sale payload like any other product and is hoisted out of the grid
 * above its section, which is what the legacy page does in `categories.js` — the same
 * `slug === 'magzter-gold'` test, the same position, the same copy. It is not a magazine
 * with a cover and a term dropdown; it is a subscription to everything, and it is priced
 * and presented as an offer.
 *
 * `useProductTile` prices it rather than a second copy of the arithmetic, so the card and
 * the tiles cannot disagree about what a term costs. It resolves the same default term
 * legacy resolved — the catalogue marks "1 Year Subscription" as the default and it is
 * also first in `restricted_terms`, so both readings agree here.
 */
export default function MagzterGoldPromo({ product, onAdd }: Props) {
  const [adding, setAdding] = useState(false);
  const { buildUrl } = useUrlGenerator();
  const { selectedTerm, price, skuCode } = useProductTile(product, false);

  if (!selectedTerm) {
    return null;
  }

  /*
   * "1 Year Subscription" is the term's own label, and the sentence needs "1 Year".
   * Legacy trimmed it the same way, in the same place, for the same reason: the catalogue
   * writes the noun into the option and the copy already supplies it.
   */
  const termLabel = selectedTerm.option.split(',')[0].split('Subscription')[0].trim();
  const msrp = applyTermPercentage(product.msrp, selectedTerm);

  const add = async () => {
    if (!skuCode) {
      return;
    }

    setAdding(true);

    try {
      // No gift sku: this has no Type variant to buy a New copy of, and legacy never
      // offered one here either.
      await onAdd(product, skuCode, null, product.coupon_code ?? null);
    } finally {
      setAdding(false);
    }
  };

  return (
    <article className="mb-4 mx-3 md:mx-0">
      <div className="bg-black rounded p-3 flex flex-col items-center justify-center text-center">
        <h3 className="m-0 max-w-lg text-xl font-black text-yellow-400">
          Unlimited Digital Access to 10,000+ magazines and newspapers
        </h3>
        <p className="m-0 mt-2 mb-3 font-medium text-white">
          {termLabel} for Only{' '}
          <span className="text-xl font-bold text-[#00F19A]">${price.toFixed(2)}</span>{' '}
          <s className="text-xs opacity-60 text-[#00F19A]">${msrp.toFixed(2)}</s>
        </p>
        <button
          type="button"
          onClick={add}
          disabled={adding || !skuCode}
          className="border-0 rounded-full bg-yellow-400 px-5 py-2 font-semibold text-black hover:bg-yellow-500 active:bg-yellow-600 disabled:opacity-50 cursor-pointer"
        >
          {adding ? 'Adding...' : 'Add to Cart'}
        </button>
        {/*
          Opens in this tab, unlike legacy's `window.open`. The customer is being sent to
          read about the offer and then come back to buy it; a popup that a phone opens as
          a whole new tab is worse at both halves of that.
        */}
        <a
          href={buildUrl(`/magzter-gold${product.coupon_code ? `?offer=${product.coupon_code}` : ''}`)}
          className="mt-2 text-sm text-yellow-400 underline"
        >
          Learn More
        </a>
        <p className="m-0 mt-2 text-xs text-white opacity-75">
          <FontAwesomeIcon icon={faShieldCheck} fixedWidth className="mr-1" />
          7-Day No Questions Asked Refund Guarantee
        </p>
      </div>
    </article>
  );
}
